import { RouterProvider } from '@tanstack/react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { Notifications } from '@mantine/notifications';
import { ModalsProvider } from '@mantine/modals';
import { Center, Loader } from '@mantine/core';
import { SignalRProvider } from '@/signalr/providers/SignalRClientProvider.tsx';
import { queryClient } from '@/shared/api/queryClient.ts';
import { router } from '@/router.ts';
import { ThemeProvider } from './ui/theme/ThemeProvider';
import { useMsalRouterAuth } from '@/features/auth/hooks/useMsalRouterAuth';
import { ApplicationInsightsProvider } from '@/shared/providers/ApplicationInsightsProvider';

interface AppProps {
  hubUrl: string;
}

/**
 * Inner component that provides auth context to router.
 * Must be rendered inside QueryClientProvider since useMsalRouterAuth uses useQueryClient.
 * Handles auth loading state before rendering router.
 */
function AppRouter() {
  const auth = useMsalRouterAuth();

  // Wait for MSAL to initialize before rendering router
  if (auth.isLoading) {
    return (
      <Center h="100vh">
        <Loader size="lg"/>
      </Center>
    );
  }

  return <RouterProvider router={router} context={{auth, queryClient}}/>;
}

/**
 * Root component that manages all providers and routing.
 * Follows TanStack Router's recommended auth integration pattern.
 */
function App({hubUrl}: AppProps) {
  return (
    <ApplicationInsightsProvider>
      <QueryClientProvider client={queryClient}>
        <SignalRProvider hubUrl={hubUrl}>
          <ThemeProvider defaultColorScheme="auto">
            <ModalsProvider>
              <Notifications position="top-right" autoClose={8000}/>
              <AppRouter/>
            </ModalsProvider>
          </ThemeProvider>
        </SignalRProvider>
      </QueryClientProvider>
    </ApplicationInsightsProvider>
  );
}

export default App;
